import { useState, useContext, useEffect, KeyboardEvent } from 'react';
import { BackButton, FavoriteIcon, SavedSearchNotification } from '@/components';
import { IconButton, InputAdornment } from '@mui/material';
import { cx } from '@emotion/css';
import schoolCelebrationIcon from '@/assets/school_celebration.png';
import { FlyerType } from '@/config/constants';
import FlyersContext from '@/context/FlyersContext';
import { TrackingData, useAnalytics } from '@/hooks/useAnalytics';
import { ContentSwitcher } from '../ContentSwitcher';
import styles from './NavbarSwitcher.styles';
import { SearchBox } from '../SearchBox';
import { BodyLargeEmphasized } from '../Typography';

interface Props {
  trackId: string;
  textSearch?: string;
  isTextBold?: boolean;
  saveSearchVisible?: boolean;
  showSearchBar?: boolean;
  navBarTitle?: string;
  newTitle?: boolean;
  setTextSearch?: (text: string) => void;
  onClickSearchInput?: () => void;
  onBackButtonClick?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  disabledTab?: FlyerType.school | FlyerType.community;
  trackingData?: TrackingData;
  tabGroupId?: string;
}

export const NavbarSwitcher: React.FC<Props> = ({
  trackId,
  textSearch = '',
  isTextBold,
  saveSearchVisible = true,
  showSearchBar = false,
  navBarTitle,
  newTitle = false,
  setTextSearch,
  onClickSearchInput,
  onBackButtonClick,
  onKeyDown,
  disabledTab,
  trackingData,
  tabGroupId,
}) => {
  const { track } = useAnalytics();
  const { activeTab, onChangeFlyerType, savedSearches } = useContext(FlyersContext);
  const [isSavedSearch, setIsSavedSearch] = useState(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);

  useEffect(() => {
    setIsSavedSearch(Boolean(textSearch && savedSearches.find(s => s.search === textSearch)));
  }, [savedSearches, setIsSavedSearch]);

  const onTabChangeFlyerType = (newFlyerType: string) => {
    if (newFlyerType) {
      onChangeFlyerType(trackingData);
    }
  };

  const onClickSaveSearch = () => {
    setShowNotification(true);
  };

  return (
    // <HeaderFixed>
    <div className={styles.container}>
      <SavedSearchNotification
        textSearch={textSearch}
        showNotification={showNotification}
        setShowNotification={setShowNotification}
      />
      <div
        className={cx(
          styles.searchBoxContainer,
          showSearchBar ? styles.withSearchSpacing : styles.withTitleSpacing
        )}
      >
        {onBackButtonClick && (
          <div className={cx(!showSearchBar && styles.backbuttonPosition)}>
            <BackButton trackId={trackId} onClick={onBackButtonClick} size={30} />
          </div>
        )}
        {showSearchBar ? (
          setTextSearch && (
            <SearchBox
              placeholder="Search..."
              onChange={setTextSearch}
              isTextBold={isTextBold}
              value={textSearch}
              onClick={onClickSearchInput}
              onKeyDown={onKeyDown}
              // endAdornment={
              //   saveSearchVisible && (
              //     <InputAdornment position="end">
              //       <IconButton onClick={onClickSaveSearch} edge="end">
              //         <FavoriteIcon isFavorite={isSavedSearch} size={24} />
              //       </IconButton>
              //     </InputAdornment>
              //   )
              // }
            />
          )
        ) : (
          <div className={styles.navBarTitleContainer}>
            <div className={styles.navBarTitle}>
              <BodyLargeEmphasized as="h1">{navBarTitle}</BodyLargeEmphasized>
            </div>
            {newTitle && (
              <div className={styles.newTitleIllustration}>
                <img
                  src={schoolCelebrationIcon}
                  width="49.43px"
                  height="61.8px"
                  alt="illustration"
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <ContentSwitcher
          trackId={trackId}
          options={[
            {
              label: 'School',
              value: FlyerType.school,
            },
            {
              label: 'Community',
              value: FlyerType.community,
            },
          ]}
          onChange={onTabChangeFlyerType}
          selected={activeTab}
          disabledTab={disabledTab}
          tabGroupId={tabGroupId}
        />
      </div>
    </div>
    // </HeaderFixed>
  );
};
